import React, { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/profile-components/Navbar";
import Topbar from "../../components/profile-components/Topbar";
import API_BASE_URL from "../../config";
import "../SalesPages.css";
import "../Settings.css";
import "../../styles/Tabs.css";

const CRM_FIELDS = [
  { key: "first_name", label: "First Name", required: true },
  { key: "last_name", label: "Last Name", required: true },
  { key: "dob", label: "Date of Birth" },
  { key: "gender", label: "Gender" },
  { key: "phone", label: "Phone" },
  { key: "email", label: "Email" },
  { key: "street_address", label: "Street Address" },
  { key: "unit", label: "Unit / Apt" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "zip_code", label: "Zip Code" },
  { key: "county", label: "County" },
  { key: "status", label: "Status" },
  { key: "source", label: "Source" },
  { key: "aor_email", label: "AOR Email" },
  { key: "notes", label: "Notes" },
  { key: "policy_coverage_type", label: "Policy Coverage Type" },
  { key: "policy_carrier", label: "Policy Carrier" },
  { key: "policy_product", label: "Policy Product" },
  { key: "policy_status", label: "Policy Status" },
  { key: "policy_number", label: "Policy Number" },
  { key: "application_date", label: "Application Date" },
  { key: "effective_date", label: "Effective Date" },
  { key: "renewal_date", label: "Renewal Date" },
  { key: "termination_date", label: "Termination Date" },
  { key: "premium", label: "Premium" }
];

const FIELD_ALIASES = {
  first_name: [
    "first_name",
    "firstname",
    "first name",
    "first",
    "fname",
    "given_name",
    "given name",
    "client_first_name",
    "lead_first_name"
  ],
  last_name: [
    "last_name",
    "lastname",
    "last name",
    "last",
    "lname",
    "surname",
    "family_name",
    "family name",
    "client_last_name",
    "lead_last_name"
  ],
  dob: [
    "dob",
    "date_of_birth",
    "date of birth",
    "birthdate",
    "birth_date",
    "birthday"
  ],
  gender: ["gender", "sex"],
  phone: [
    "phone",
    "phone_number",
    "phone number",
    "mobile",
    "cell",
    "cell_phone",
    "cell phone",
    "primary_phone",
    "primary phone"
  ],
  email: [
    "email",
    "email_address",
    "email address",
    "e-mail",
    "e_mail"
  ],
  street_address: [
    "street_address",
    "street address",
    "address",
    "address1",
    "address_1",
    "address line 1",
    "address_line_1",
    "street",
    "mailing_address",
    "mailing address"
  ],
  unit: [
    "unit",
    "apt",
    "apartment",
    "suite",
    "unit_number",
    "unit number",
    "address2",
    "address_2",
    "address line 2",
    "address_line_2"
  ],
  city: ["city", "town"],
  state: ["state", "st", "province"],
  zip_code: [
    "zip",
    "zip_code",
    "zip code",
    "zipcode",
    "postal",
    "postal_code",
    "postal code"
  ],
  county: ["county", "county_name", "county name"],
  status: [
    "status",
    "lead_status",
    "lead status",
    "client_status",
    "client status"
  ],
  source: [
    "source",
    "lead_source",
    "lead source",
    "referral_source",
    "referral source"
  ],
  aor_email: [
    "aor_email",
    "aor email",
    "agent_email",
    "agent email",
    "assigned_agent_email",
    "assigned agent email",
    "owner_email",
    "owner email"
  ],
  notes: ["notes", "note", "comments", "comment", "remarks"],
  policy_coverage_type: [
    "policy_coverage_type",
    "policy coverage type",
    "coverage_type",
    "coverage type",
    "product_type",
    "product type",
    "line_of_business",
    "line of business",
    "lob"
  ],
  policy_carrier: [
    "policy_carrier",
    "policy carrier",
    "carrier",
    "carrier_name",
    "carrier name",
    "company",
    "insurance_company",
    "insurance company"
  ],
  policy_product: [
    "policy_product",
    "policy product",
    "product",
    "plan",
    "plan_name",
    "plan name",
    "product_name",
    "product name"
  ],
  policy_status: [
    "policy_status",
    "policy status",
    "policy_stage",
    "policy stage"
  ],
  policy_number: [
    "policy_number",
    "policy number",
    "policy_no",
    "policy no",
    "policy id",
    "policy_id",
    "member_id",
    "member id"
  ],
  application_date: [
    "application_date",
    "application date",
    "app_date",
    "app date",
    "submitted_date",
    "submitted date"
  ],
  effective_date: [
    "effective_date",
    "effective date",
    "eff_date",
    "eff date",
    "start_date",
    "start date"
  ],
  renewal_date: [
    "renewal_date",
    "renewal date",
    "renew_date",
    "renew date"
  ],
  termination_date: [
    "termination_date",
    "termination date",
    "term_date",
    "term date",
    "cancel_date",
    "cancel date",
    "end_date",
    "end date"
  ],
  premium: [
    "premium",
    "monthly_premium",
    "monthly premium",
    "amount",
    "payment",
    "rate"
  ]
};

const PREVIEW_LIMIT = 25;

function normalizeHeader(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^\uFEFF/, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
}

function parseCsvLine(line) {
  const values = [];
  let current = "";
  let insideQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"' && insideQuotes && nextChar === '"') {
      current += '"';
      i += 1;
    } else if (char === '"') {
      insideQuotes = !insideQuotes;
    } else if (char === "," && !insideQuotes) {
      values.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }

  values.push(current.trim());
  return values;
}

function parseCsvText(text) {
  const cleanText = String(text || "").replace(/^\uFEFF/, "");
  const lines = cleanText
    .replace(/\r/g, "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  if (!lines.length) {
    return {
      headers: [],
      rows: [],
      errors: ["CSV file is empty."]
    };
  }

  if (lines.length < 2) {
    return {
      headers: [],
      rows: [],
      errors: ["CSV must include a header row and at least one data row."]
    };
  }

  const headers = parseCsvLine(lines[0]).map((header) => String(header || "").trim());

  const rows = lines.slice(1).map((line, index) => {
    const values = parseCsvLine(line);
    const row = {};

    headers.forEach((header, headerIndex) => {
      row[header] = values[headerIndex] || "";
    });

    return {
      rowNumber: index + 2,
      ...row
    };
  });

  return {
    headers,
    rows,
    errors: []
  };
}

function buildAutoMapping(headers) {
  const mapping = {};
  const normalizedHeaderMap = {};

  headers.forEach((header) => {
    normalizedHeaderMap[normalizeHeader(header)] = header;
  });

  CRM_FIELDS.forEach((field) => {
    const aliases = FIELD_ALIASES[field.key] || [field.key];

    const matchedAlias = aliases.find((alias) =>
      Object.prototype.hasOwnProperty.call(
        normalizedHeaderMap,
        normalizeHeader(alias)
      )
    );

    mapping[field.key] = matchedAlias
      ? normalizedHeaderMap[normalizeHeader(matchedAlias)]
      : "";
  });

  return mapping;
}

function applyColumnMapping(rows, columnMapping) {
  return rows.map((row) => {
    const mapped = {};

    CRM_FIELDS.forEach((field) => {
      const sourceColumn = columnMapping[field.key];
      mapped[field.key] = sourceColumn ? row[sourceColumn] || "" : "";
    });

    return mapped;
  });
}

const ImportContacts = () => {
  const [activeTab, setActiveTab] = useState("Import");

  const [importType, setImportType] = useState("lead");
  const [rawHeaders, setRawHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [importFileName, setImportFileName] = useState("");
  const [importErrors, setImportErrors] = useState([]);

  const [preview, setPreview] = useState(null);
  const [confirmResult, setConfirmResult] = useState(null);

  const [showImportModal, setShowImportModal] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [confirmingImport, setConfirmingImport] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const importFileInputRef = useRef(null);
  const token = localStorage.getItem("token");

  const mappedRows = useMemo(() => {
    return applyColumnMapping(rawRows, columnMapping);
  }, [rawRows, columnMapping]);

  const missingRequiredMappings = useMemo(() => {
    return CRM_FIELDS.filter((field) => field.required && !columnMapping[field.key]);
  }, [columnMapping]);

  const importStats = useMemo(() => {
    return {
      rows: rawRows.length,
      validRows: preview?.validCount || 0,
      invalidRows: preview?.invalidCount || 0,
      policies: preview?.policyCount || 0
    };
  }, [rawRows.length, preview]);

  const canPreviewImport = Boolean(
    rawRows.length &&
      !importErrors.length &&
      missingRequiredMappings.length === 0
  );

  const canConfirmImport = Boolean(
    preview &&
      preview.validCount > 0 &&
      preview.invalidCount === 0 &&
      !confirmResult
  );

  const templateCsv = useMemo(() => {
    const headers = CRM_FIELDS.map((field) => field.key);
    const exampleRow = [
      "John",
      "Smith",
      "1955-01-15",
      "Male",
      "555-555-5555",
      "john@example.com",
      "123 Main St",
      "Apt 2",
      "Kansas City",
      "MO",
      "64129",
      "Jackson",
      "New",
      "Referral",
      "agent@example.com",
      "Imported contact",
      "MAPD",
      "Humana",
      "Humana Gold Plus",
      "Active",
      "ABC123",
      "2026-01-01",
      "2026-02-01",
      "2027-02-01",
      "",
      "0"
    ];

    return `${headers.join(",")}\n${exampleRow
      .map((value) => `"${String(value).replace(/"/g, '""')}"`)
      .join(",")}`;
  }, []);

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const resetImportState = () => {
    setShowImportModal(false);
    setRawHeaders([]);
    setRawRows([]);
    setColumnMapping({});
    setImportErrors([]);
    setImportFileName("");
    setPreview(null);
    setConfirmResult(null);

    if (importFileInputRef.current) {
      importFileInputRef.current.value = "";
    }
  };

  const downloadCsvTemplate = () => {
    const blob = new Blob([templateCsv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = "lead-client-import-template.csv";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  const handleImportFileChange = async (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    try {
      clearMessages();
      setPreview(null);
      setConfirmResult(null);
      setImportFileName(file.name);
      setRawHeaders([]);
      setRawRows([]);
      setColumnMapping({});
      setImportErrors([]);

      if (!file.name.toLowerCase().endsWith(".csv")) {
        setImportErrors(["Please upload a CSV file."]);
        return;
      }

      const text = await file.text();
      const parsed = parseCsvText(text);

      setRawHeaders(parsed.headers);
      setRawRows(parsed.rows);
      setImportErrors(parsed.errors);

      if (!parsed.errors.length) {
        setColumnMapping(buildAutoMapping(parsed.headers));
      }
    } catch (err) {
      console.error("CSV parse error:", err);
      setImportErrors([err.message || "Failed to parse CSV file."]);
    }
  };

  const handleMappingChange = (crmFieldKey, sourceColumn) => {
    setColumnMapping((prev) => ({
      ...prev,
      [crmFieldKey]: sourceColumn
    }));

    setPreview(null);
    setConfirmResult(null);
    clearMessages();
  };

  const handlePreviewImport = async () => {
    if (!rawRows.length) {
      alert("Please upload a CSV file first.");
      return;
    }

    if (importErrors.length) {
      alert("Please fix the CSV errors before previewing.");
      return;
    }

    if (missingRequiredMappings.length) {
      alert(
        `Please map required field(s): ${missingRequiredMappings
          .map((field) => field.label)
          .join(", ")}`
      );
      return;
    }

    try {
      setLoadingPreview(true);
      clearMessages();
      setPreview(null);
      setConfirmResult(null);

      const res = await fetch(`${API_BASE_URL}/api/imports/contacts/preview`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          importType,
          rows: mappedRows
        })
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.message || "Failed to preview import.");
      }

      setPreview(data);

      if (data.invalidCount > 0) {
        setError("Some rows have errors. Fix the mapping or CSV and preview again before importing.");
      } else {
        setSuccess("Preview complete. Review the rows, then confirm the import.");
      }
    } catch (err) {
      console.error("Import preview error:", err);
      setError(err.message || "Failed to preview import.");
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!canConfirmImport) {
      alert("Preview a valid CSV before confirming import.");
      return;
    }

    const confirmed = window.confirm(
      `Import ${preview.validCount} ${importType === "client" ? "client" : "lead"} record(s)?`
    );

    if (!confirmed) return;

    try {
      setConfirmingImport(true);
      clearMessages();

      const res = await fetch(`${API_BASE_URL}/api/imports/contacts/confirm`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          importType,
          rows: mappedRows
        })
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.message || "Failed to confirm import.");
      }

      setConfirmResult(data);
      setShowImportModal(false);
      setSuccess(buildImportSuccessMessage(data));
    } catch (err) {
      console.error("Confirm import error:", err);
      setError(err.message || "Failed to confirm import.");
    } finally {
      setConfirmingImport(false);
    }
  };

  const buildImportSuccessMessage = (data) => {
    return [
      "Import complete.",
      `Records created: ${data.summary?.recordsCreated || 0}.`,
      `Policies created: ${data.summary?.policiesCreated || 0}.`,
      `Notes created: ${data.summary?.notesCreated || 0}.`,
      `Duplicates skipped: ${data.summary?.recordsSkippedAsDuplicates || 0}.`,
      `Rows skipped: ${data.skippedCount || 0}.`
    ].join(" ");
  };

  const renderImportTab = () => (
    <div className="settings-modern-stack">
      <div className="sales-section-card sales-section-subscription">
        <div className="sales-section-head">
          <h2>Import Contacts</h2>
          <p>Upload any CSV, map its columns to CRM fields, preview the results, then confirm the import.</p>
        </div>

        <div className="sales-form-fields">
          <div className="sales-field">
            <label>Import Type</label>
            <select
              value={importType}
              onChange={(e) => {
                setImportType(e.target.value);
                setPreview(null);
                setConfirmResult(null);
                clearMessages();
              }}
            >
              <option value="lead">Import as Leads</option>
              <option value="client">Import as Clients</option>
            </select>
          </div>

          <div className="sales-field">
            <label>Selected File</label>
            <input value={importFileName || "No file selected"} readOnly />
          </div>
        </div>

        <div
          style={{
            marginTop: "14px",
            padding: "12px 14px",
            borderRadius: "14px",
            border: "1px solid #dbe4f0",
            background: "#f8fafc",
            color: "#475569",
            lineHeight: 1.5
          }}
        >
          Only <strong>First Name</strong> and <strong>Last Name</strong> are required. All other
          contact fields are optional. Client imports can also create policies when policy fields
          are mapped and populated.
        </div>

        <div className="settings-inline-actions" style={{ marginTop: "14px" }}>
          <button
            className="sales-primary-btn"
            type="button"
            onClick={() => {
              setShowImportModal(true);
              clearMessages();
            }}
          >
            Import CSV
          </button>

          <button className="sales-secondary-btn" type="button" onClick={downloadCsvTemplate}>
            Download Template
          </button>

          <button
            className="sales-secondary-btn"
            type="button"
            onClick={resetImportState}
            disabled={!rawRows.length && !preview && !confirmResult}
          >
            Clear Import
          </button>
        </div>
      </div>

      {preview && renderPreviewCard()}

      {confirmResult && renderResultsCard()}
    </div>
  );

  const renderTemplateTab = () => (
    <div className="settings-modern-stack">
      <div className="sales-section-card sales-section-lead">
        <div className="sales-section-head">
          <h2>CRM Import Fields</h2>
          <p>You can map your CSV columns to these CRM fields during import.</p>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table className="sales-table" style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "10px" }}>CRM Field</th>
                <th style={{ textAlign: "left", padding: "10px" }}>Required?</th>
                <th style={{ textAlign: "left", padding: "10px" }}>Internal Column</th>
              </tr>
            </thead>

            <tbody>
              {CRM_FIELDS.map((field) => (
                <tr key={field.key} style={{ borderTop: "1px solid #e2e8f0" }}>
                  <td style={{ padding: "10px" }}>{field.label}</td>
                  <td style={{ padding: "10px" }}>{field.required ? "Yes" : "No"}</td>
                  <td style={{ padding: "10px" }}>
                    <code>{field.key}</code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="settings-inline-actions" style={{ marginTop: "14px" }}>
          <button className="sales-primary-btn" type="button" onClick={downloadCsvTemplate}>
            Download CSV Template
          </button>
        </div>
      </div>

      <div className="sales-section-card sales-section-subscription">
        <div className="sales-section-head">
          <h2>Policy Import Rules</h2>
          <p>Policy rows are only created when importing as clients.</p>
        </div>

        <div
          style={{
            padding: "12px 14px",
            borderRadius: "14px",
            border: "1px solid #dbe4f0",
            background: "#f8fafc",
            color: "#475569",
            lineHeight: 1.6
          }}
        >
          One CSV row represents one person and optional one policy. If a client has multiple
          policies, include that client on multiple rows with different policy details.
        </div>
      </div>
    </div>
  );

  const renderPreviewCard = () => (
    <div className="sales-section-card sales-section-lead">
      <div className="sales-section-head">
        <h2>Import Preview</h2>
        <p>
          {preview.validCount || 0} valid / {preview.invalidCount || 0} invalid /{" "}
          {preview.policyCount || 0} policies detected
        </p>
      </div>

      <div style={{ overflowX: "auto", maxHeight: "360px", overflowY: "auto" }}>
        <table className="sales-table" style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "10px" }}>Row</th>
              <th style={{ textAlign: "left", padding: "10px" }}>Status</th>
              <th style={{ textAlign: "left", padding: "10px" }}>Name</th>
              <th style={{ textAlign: "left", padding: "10px" }}>Policy?</th>
              <th style={{ textAlign: "left", padding: "10px" }}>Errors</th>
              <th style={{ textAlign: "left", padding: "10px" }}>Warnings</th>
            </tr>
          </thead>

          <tbody>
            {(preview.results || []).slice(0, PREVIEW_LIMIT).map((result) => (
              <tr key={result.rowNumber} style={{ borderTop: "1px solid #e2e8f0" }}>
                <td style={{ padding: "10px" }}>{result.rowNumber}</td>
                <td style={{ padding: "10px" }}>{result.valid ? "Valid" : "Invalid"}</td>
                <td style={{ padding: "10px" }}>
                  {result.normalized?.first_name} {result.normalized?.last_name}
                </td>
                <td style={{ padding: "10px" }}>{result.willCreatePolicy ? "Yes" : "No"}</td>
                <td style={{ padding: "10px" }}>{(result.errors || []).join("; ") || "-"}</td>
                <td style={{ padding: "10px" }}>{(result.warnings || []).join("; ") || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(preview.results || []).length > PREVIEW_LIMIT && (
        <p style={{ color: "#64748b" }}>
          Showing first {PREVIEW_LIMIT} rows only. All valid rows will be imported.
        </p>
      )}

      <div className="settings-inline-actions" style={{ marginTop: "14px" }}>
        <button
          type="button"
          className="sales-primary-btn"
          onClick={handleConfirmImport}
          disabled={!canConfirmImport || confirmingImport}
        >
          {confirmingImport ? "Importing..." : "Confirm Import"}
        </button>

        <button
          type="button"
          className="sales-secondary-btn"
          onClick={() => {
            setPreview(null);
            setConfirmResult(null);
            clearMessages();
          }}
          disabled={confirmingImport}
        >
          Clear Preview
        </button>
      </div>
    </div>
  );

  const renderResultsCard = () => (
    <div className="sales-section-card sales-section-subscription">
      <div className="sales-section-head">
        <h2>Import Results</h2>
        <p>
          {confirmResult.importedCount || 0} imported / {confirmResult.skippedCount || 0} skipped
        </p>
      </div>

      <div className="sales-form-fields">
        <div className="sales-field">
          <label>Records Created</label>
          <input value={confirmResult.summary?.recordsCreated || 0} readOnly />
        </div>

        <div className="sales-field">
          <label>Duplicates Skipped</label>
          <input value={confirmResult.summary?.recordsSkippedAsDuplicates || 0} readOnly />
        </div>

        <div className="sales-field">
          <label>Policies Created</label>
          <input value={confirmResult.summary?.policiesCreated || 0} readOnly />
        </div>

        <div className="sales-field">
          <label>Policies Matched to Products</label>
          <input value={confirmResult.summary?.policiesMatchedToProducts || 0} readOnly />
        </div>

        <div className="sales-field">
          <label>Policies Without Product Match</label>
          <input value={confirmResult.summary?.policiesWithoutProductMatch || 0} readOnly />
        </div>

        <div className="sales-field">
          <label>Notes Created</label>
          <input value={confirmResult.summary?.notesCreated || 0} readOnly />
        </div>
      </div>

      {Array.isArray(confirmResult.skipped) && confirmResult.skipped.length > 0 && (
        <div style={{ overflowX: "auto", marginTop: "16px" }}>
          <table className="sales-table" style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "10px" }}>Row</th>
                <th style={{ textAlign: "left", padding: "10px" }}>Reason</th>
                <th style={{ textAlign: "left", padding: "10px" }}>Details</th>
              </tr>
            </thead>

            <tbody>
              {confirmResult.skipped.slice(0, PREVIEW_LIMIT).map((item) => (
                <tr key={`${item.rowNumber}-${item.reason}`} style={{ borderTop: "1px solid #e2e8f0" }}>
                  <td style={{ padding: "10px" }}>{item.rowNumber}</td>
                  <td style={{ padding: "10px" }}>{item.reason}</td>
                  <td style={{ padding: "10px" }}>
                    {(item.errors || []).join("; ") || item.name || item.existingId || "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderMappingRows = () => (
    <div style={{ overflowX: "auto", maxHeight: "360px", overflowY: "auto" }}>
      <table className="sales-table" style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ textAlign: "left", padding: "10px" }}>CRM Field</th>
            <th style={{ textAlign: "left", padding: "10px" }}>CSV Column</th>
          </tr>
        </thead>

        <tbody>
          {CRM_FIELDS.map((field) => (
            <tr key={field.key} style={{ borderTop: "1px solid #e2e8f0" }}>
              <td style={{ padding: "10px", fontWeight: field.required ? 800 : 500 }}>
                {field.label}
                {field.required ? " *" : ""}
              </td>

              <td style={{ padding: "10px" }}>
                <select
                  value={columnMapping[field.key] || ""}
                  onChange={(e) => handleMappingChange(field.key, e.target.value)}
                >
                  <option value="">Do not import</option>
                  {rawHeaders.map((header) => (
                    <option key={`${field.key}-${header}`} value={header}>
                      {header}
                    </option>
                  ))}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderImportModal = () => {
    if (!showImportModal) return null;

    return (
      <div className="modal-overlay" onClick={() => setShowImportModal(false)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <h2>Import Leads / Clients</h2>

          <p style={{ color: "#64748b", lineHeight: 1.6 }}>
            Upload a CSV file, map its columns to CRM fields, then preview the import before
            anything is saved.
          </p>

          <div
            style={{
              border: "1px solid #bfdbfe",
              background: "#eff6ff",
              color: "#1e3a8a",
              borderRadius: "12px",
              padding: "12px 14px",
              marginBottom: "14px",
              lineHeight: 1.5
            }}
          >
            Current import type: <strong>{importType === "client" ? "Clients" : "Leads"}</strong>
          </div>

          <div className="settings-inline-actions" style={{ marginBottom: "14px" }}>
            <button className="sales-secondary-btn" type="button" onClick={downloadCsvTemplate}>
              Download Template
            </button>

            <button
              className="sales-secondary-btn"
              type="button"
              onClick={() => importFileInputRef.current?.click()}
            >
              Choose CSV File
            </button>
          </div>

          <input
            ref={importFileInputRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: "none" }}
            onChange={handleImportFileChange}
          />

          {importFileName && (
            <p style={{ color: "#334155" }}>
              Selected file: <strong>{importFileName}</strong>
            </p>
          )}

          {importErrors.length > 0 && (
            <div
              style={{
                border: "1px solid #fecaca",
                background: "#fef2f2",
                color: "#991b1b",
                borderRadius: "12px",
                padding: "12px 14px",
                marginBottom: "14px"
              }}
            >
              {importErrors.map((importError, index) => (
                <div key={index}>{importError}</div>
              ))}
            </div>
          )}

          {rawRows.length > 0 && (
            <>
              <div style={{ marginTop: "14px" }}>
                <h3>Column Mapping</h3>
                <p style={{ color: "#64748b" }}>
                  {rawRows.length} row(s) found. Map at least First Name and Last Name.
                </p>

                {missingRequiredMappings.length > 0 && (
                  <div
                    style={{
                      border: "1px solid #fecaca",
                      background: "#fef2f2",
                      color: "#991b1b",
                      borderRadius: "12px",
                      padding: "12px 14px",
                      marginBottom: "14px"
                    }}
                  >
                    Missing required mapping:{" "}
                    {missingRequiredMappings.map((field) => field.label).join(", ")}
                  </div>
                )}

                {renderMappingRows()}
              </div>

              <div style={{ marginTop: "18px" }}>
                <h3>File Preview</h3>
                <p style={{ color: "#64748b" }}>
                  Showing mapped values for the first {Math.min(PREVIEW_LIMIT, rawRows.length)} row(s).
                </p>

                <div style={{ overflowX: "auto", maxHeight: "240px", overflowY: "auto" }}>
                  <table className="sales-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: "left", padding: "10px" }}>Row</th>
                        <th style={{ textAlign: "left", padding: "10px" }}>First Name</th>
                        <th style={{ textAlign: "left", padding: "10px" }}>Last Name</th>
                        <th style={{ textAlign: "left", padding: "10px" }}>Email</th>
                        <th style={{ textAlign: "left", padding: "10px" }}>Phone</th>
                        <th style={{ textAlign: "left", padding: "10px" }}>Policy Type</th>
                      </tr>
                    </thead>

                    <tbody>
                      {mappedRows.slice(0, PREVIEW_LIMIT).map((row, index) => (
                        <tr key={index} style={{ borderTop: "1px solid #e2e8f0" }}>
                          <td style={{ padding: "10px" }}>{rawRows[index]?.rowNumber || index + 2}</td>
                          <td style={{ padding: "10px" }}>{row.first_name || "-"}</td>
                          <td style={{ padding: "10px" }}>{row.last_name || "-"}</td>
                          <td style={{ padding: "10px" }}>{row.email || "-"}</td>
                          <td style={{ padding: "10px" }}>{row.phone || "-"}</td>
                          <td style={{ padding: "10px" }}>{row.policy_coverage_type || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={() => setShowImportModal(false)}>
              Cancel
            </button>

            <button
              type="button"
              className="btn-save"
              onClick={async () => {
                await handlePreviewImport();
                setShowImportModal(false);
              }}
              disabled={loadingPreview || !canPreviewImport}
            >
              {loadingPreview ? "Previewing..." : "Preview Import"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page settings-page-modern">
        <div className="sales-hero sales-profile-hero">
          <div>
            <p className="sales-eyebrow">Settings</p>
            <h1 className="sales-title">Import Leads / Clients</h1>
            <p className="sales-subtitle">
              Import lead and client records from CSV files with flexible column mapping.
            </p>
          </div>

          <div className="sales-header-actions">
            <Link
              className="sales-secondary-btn"
              to="/settings?tab=Data%20%26%20Customization"
              style={{ textDecoration: "none" }}
            >
              ← Back to Data & Customization
            </Link>

            <button className="sales-primary-btn" onClick={() => setShowImportModal(true)}>
              Import CSV
            </button>
          </div>
        </div>

        {(error || success) && (
          <div
            className="sales-section-card"
            style={{
              borderColor: error ? "#fecaca" : "#bbf7d0",
              background: error ? "#fef2f2" : "#f0fdf4",
              color: error ? "#991b1b" : "#166534",
              marginBottom: "18px"
            }}
          >
            {error || success}
          </div>
        )}

        <div className="sales-profile-grid" style={{ marginBottom: "18px" }}>
          <div className="sales-section-card sales-section-lead">
            <div className="sales-section-head">
              <h2>Rows Loaded</h2>
              <p>{importStats.rows}</p>
            </div>
          </div>

          <div className="sales-section-card sales-section-subscription">
            <div className="sales-section-head">
              <h2>Valid Rows</h2>
              <p>{importStats.validRows}</p>
            </div>
          </div>

          <div className="sales-section-card sales-section-lead">
            <div className="sales-section-head">
              <h2>Policies Detected</h2>
              <p>{importStats.policies}</p>
            </div>
          </div>
        </div>

        <div className="tabs">
          {["Import", "Template"].map((tab) => (
            <div
              key={tab}
              className={`tab ${activeTab === tab ? "active" : ""}`}
              onClick={() => {
                setActiveTab(tab);
                clearMessages();
              }}
            >
              {tab}
            </div>
          ))}
        </div>

        <div className="tab-content settings-tab-modern">
          {activeTab === "Import" ? renderImportTab() : renderTemplateTab()}
        </div>
      </div>

      {renderImportModal()}
    </>
  );
};

export default ImportContacts;